<template>
	<app-menu>
		<app-menu-item icon="plus" @click="openAddProduct = true"> Add Product </app-menu-item>
		<app-menu-item icon="trash" @click="showDeleteConfirm = true"> Delete Type </app-menu-item>
	</app-menu>

	<confirm-dialog
		:open="showDeleteConfirm"
		title="Delete Product Type"
		button="Delete"
		theme="danger"
		:hide-button="hasAssociatedProducts"
		@confirmed="deleteProductType"
		@canceled="showDeleteConfirm = false">
		<div v-if="hasAssociatedProducts" class="space-y-3">
			<p class="text-red-600 font-semibold">Cannot delete this product type</p>
			<p>
				This product type has {{ productType?.products?.length || 0 }} associated
				product(s). Before deleting this type, you must:
			</p>
			<ul class="list-disc list-inside space-y-1 ml-2">
				<li>Move the products to another category type, or</li>
				<li>Delete the products</li>
			</ul>
		</div>
		<p v-else>
			Are you sure you want to delete <strong>{{ productType?.name }}</strong>
			>? This action cannot be undone.
		</p>
	</confirm-dialog>

	<slideover title="Add Product" :open="openAddProduct" @close="closeAddProduct">
		<div v-if="apiError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
			<div class="flex">
				<div class="flex-shrink-0">
					<svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
						<path
							fill-rule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
							clip-rule="evenodd" />
					</svg>
				</div>
				<div class="ml-3">
					<h3 class="text-sm font-medium text-red-800">Error</h3>
					<div class="mt-2 text-sm text-red-700">
						<p>{{ apiError }}</p>
					</div>
				</div>
			</div>
		</div>

		<app-input
			label="Name"
			:model-value="productName"
			:disabled="submittingProduct"
			:error="productNameError"
			@update:model-value="productName = $event; clearApiError()"
			@blur="productNameBlur">
		</app-input>

		<app-input
			label="Summary"
			type="textarea"
			:model-value="productSummary"
			:disabled="submittingProduct"
			:error="productSummaryError"
			@update:model-value="productSummary = $event; clearApiError()"
			@blur="productSummaryBlur">
		</app-input>

		<app-input
			label="Description"
			type="textarea"
			:model-value="productDescription"
			:disabled="submittingProduct"
			:error="productDescriptionError"
			@update:model-value="productDescription = $event; clearApiError()"
			@blur="productDescriptionBlur">
		</app-input>

		<app-input
			label="Features & Benefits"
			type="textarea"
			:model-value="productFeaturesBenefits"
			:disabled="submittingProduct"
			:error="productFeaturesBenefitsError"
			@update:model-value="productFeaturesBenefits = $event; clearApiError()"
			@blur="productFeaturesBenefitsBlur">
		</app-input>

		<template #action>
			<div class="flex justify-end w-full">
				<app-button @click="submitProduct">Save</app-button>
			</div>
		</template>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, computed } from 'vue'
	import { useForm, useField } from 'vee-validate'
	import * as Yup from 'yup'
	import type { ProductType } from '@/data/schema/product-type'
	import ProductTypeService from '@/services/product-type'
	import ProductService from '@/services/product'
	import useToaster from '@/stores/toaster'
	import ConfirmDialog from '@/components/partials/dialogs/Confirm.vue'
	import AppMenu from '@/components/base/AppMenu.vue'
	import AppMenuItem from '@/components/base/AppMenuItem.vue'

	const deleting = ref<boolean>(false)
	const showDeleteConfirm = ref(false)
	const openAddProduct = ref(false)
	const submittingProduct = ref<boolean>(false)
	const apiError = ref<string>('')

	const props = defineProps<{
		productType: ProductType
	}>()

	const toaster = useToaster()
	const productTypeService = new ProductTypeService()
	const productService = new ProductService()

	const emit = defineEmits(['completed'])

	const hasAssociatedProducts = computed(() => {
		return props.productType?.products && props.productType.products.length > 0
	})

	const {
		resetForm: resetProductForm,
		handleSubmit: handleProductSubmit,
		setFieldError: setProductFieldError
	} = useForm<{
		type: string
		name: string
		summary: string
		description: string
		features_benefits: string
	}>({
		initialValues: {
			type: props.productType?.id ?? '',
			name: '',
			summary: '',
			description: '',
			features_benefits: ''
		},
		validationSchema: Yup.object({
			name: Yup.string()
				.min(5, 'Name must be at least 5 characters')
				.max(255, 'Name must be at most 255 characters')
				.required('Name is required'),
			summary: Yup.string()
				.nullable()
				.min(5, 'Summary must be at least 5 characters')
				.max(255, 'Summary must be at most 255 characters'),
			description: Yup.string()
				.nullable()
				.min(5, 'Description must be at least 5 characters')
				.max(65535, 'Description has reached maximum allowed limit (65535 characters)'),
			features_benefits: Yup.string()
				.nullable()
				.min(5, 'Features & Benefits must be at least 5 characters')
				.max(65535, 'Features & Benefits has reached maximum allowed limit (65535 characters)')
		}),
		validateOnMount: false,
		validateOnBlur: true
	})

	const {
		value: productName,
		errorMessage: productNameError,
		handleBlur: productNameBlur
	} = useField('name')
	const {
		value: productSummary,
		errorMessage: productSummaryError,
		handleBlur: productSummaryBlur
	} = useField('summary')
	const {
		value: productDescription,
		errorMessage: productDescriptionError,
		handleBlur: productDescriptionBlur
	} = useField('description')
	const {
		value: productFeaturesBenefits,
		errorMessage: productFeaturesBenefitsError,
		handleBlur: productFeaturesBenefitsBlur
	} = useField('features_benefits')

	const submitProduct = handleProductSubmit(
		(values) => {
			submittingProduct.value = true

			productService
				.create(values, { expand: 'type' })
				.then((result) => {
					toaster.success({ text: values.name + ' created' })
					closeAddProduct()
					emit('completed', result.data)
				})
				.catch((error) => {
					console.error('Error creating product:', error)

					// Handle API validation errors
					if (error.response?.data?.errors) {
						const apiErrors = error.response.data.errors

						// Set field errors from API response
						if (apiErrors.name) {
							setProductFieldError('name', apiErrors.name[0])
							apiError.value = apiErrors.name[0]
						}

						toaster.error({ text: 'Validation failed. Please check the form.' })
					} else if (error.response?.data?.name) {
						// Handle case where name error is at the root level
						const nameError = Array.isArray(error.response.data.name)
							? error.response.data.name[0]
							: error.response.data.name
						setProductFieldError('name', nameError)
						apiError.value = nameError
						toaster.error({ text: 'Validation failed. Please check the form.' })
					} else {
						apiError.value = 'Failed to create product. Please try again.'
						toaster.error({ text: 'Failed to create product' })
					}
				})
				.finally(() => {
					submittingProduct.value = false
				})
		},
		(errors) => {
			console.error(errors)
		}
	)

	function closeAddProduct() {
		openAddProduct.value = false
		resetProductForm()
		apiError.value = ''
	}

	function clearApiError() {
		if (apiError.value) {
			apiError.value = ''
		}
	}

	const deleteProductType = async () => {
		if (!props.productType?.id || hasAssociatedProducts.value) return

		deleting.value = true
		try {
			await productTypeService.delete(props.productType.id)
			toaster.success({ text: props.productType.name + ' deleted successfully' })
			showDeleteConfirm.value = false
			emit('completed', false)
		} catch (error) {
			console.error('Error deleting product type:', error)
			toaster.error({ text: 'Failed to delete product type' })
		} finally {
			deleting.value = false
		}
	}
</script>
